import { supabase } from './supabase';
import type { ExportConfig, SavedExportConfig, ExportJob } from '../types';

const API_BASE = ''; // Same domain as your app

async function apiCall(endpoint: string, options: RequestInit = {}) {
    const { data: { session } } = await supabase.auth.getSession();

    // Ensure we have a valid session before making API calls
    if (!session?.access_token) {
        throw new Error('No authentication token available');
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
            ...options.headers,
        },
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(error.error || 'API call failed');
    }

    return response.json();
}

// Project API
export const projectsApi = {
    list: (): Promise<{ projects: any[] }> => apiCall('/api/projects'),
    create: (data: { name: string; description?: string }): Promise<{ project: any }> =>
        apiCall('/api/projects', { method: 'POST', body: JSON.stringify(data) }),
};

// Folders API
export const foldersApi = {
    list: (projectId: string): Promise<{ folders: any[] }> => apiCall(`/api/folders?projectId=${projectId}`),
    create: (data: { name: string; projectId: string; parentFolderId?: string; icon?: string }): Promise<{ folder: any }> =>
        apiCall('/api/folders', { method: 'POST', body: JSON.stringify(data) }),
    update: (folderId: string, data: { name?: string; icon?: string }): Promise<{ folder: any }> =>
        apiCall(`/api/folders?id=${folderId}`, { method: 'PUT', body: JSON.stringify(data) }),
};

// Sounds API
export const soundsApi = {
    list: (projectId?: string, folderId?: string) => {
        const params = new URLSearchParams();
        if (projectId) params.set('projectId', projectId);
        if (folderId) params.set('folderId', folderId);
        return apiCall(`/api/sounds?${params.toString()}`);
    },
    generate: (data: {
        description: string;
        projectId: string;
        folderId?: string;
        duration?: number;
        name?: string;
    }) => apiCall('/api/generate-sound', { method: 'POST', body: JSON.stringify(data) }),
    download: async (downloadUrl: string): Promise<Blob> => {
        // Always get a fresh session to ensure token is valid
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
            console.error('Session error:', sessionError);
            throw new Error('Failed to get authentication session');
        }
        
        if (!session?.access_token) {
            // Try to refresh the session
            console.log('No access token, attempting to refresh session...');
            const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();
            
            if (refreshError || !refreshedSession?.access_token) {
                console.error('Failed to refresh session:', refreshError);
                throw new Error('Authentication required. Please sign in again.');
            }
            
            console.log('Session refreshed successfully');
        }
        
        const token = session?.access_token || (await supabase.auth.getSession()).data.session?.access_token;
        if (!token) {
            throw new Error('No authentication token available');
        }
        
        console.log('Fetching download with token:', token.substring(0, 20) + '...');
        console.log('Download URL:', downloadUrl);
        
        const response = await fetch(downloadUrl, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Accept': 'audio/mpeg, audio/*, */*',
                'Cache-Control': 'no-cache',
            },
            // Ensure credentials are included
            credentials: 'same-origin',
        });
        
        console.log('Download response status:', response.status);
        console.log('Download response headers:', Object.fromEntries(response.headers.entries()));
        
        if (!response.ok) {
            let errorMessage = `Download failed with status: ${response.status}`;
            try {
                // Clone response before reading to avoid consuming body twice
                const clonedResponse = response.clone();
                const errorData = await clonedResponse.json();
                errorMessage = errorData.error || errorMessage;
            } catch {
                // If response is not JSON, try to get text
                try {
                    const errorText = await response.text();
                    if (errorText) {
                        errorMessage = `${errorMessage} - ${errorText}`;
                    }
                } catch {
                    // Ignore if we can't read the response
                }
            }
            console.error('Download error:', errorMessage);
            throw new Error(errorMessage);
        }
        
        const blob = await response.blob();
        console.log('Blob created successfully, size:', blob.size, 'type:', blob.type);
        
        return blob;
    },
    delete: (soundId: string): Promise<{ success: boolean; message: string }> =>
        apiCall(`/api/sounds?id=${soundId}`, { method: 'DELETE' }),
};

// Stripe/Subscription API
export const subscriptionApi = {
    getSubscription: (): Promise<{ subscription: any }> => apiCall('/api/subscription'),
    createCheckout: (data: { 
        priceId: string; 
        successUrl?: string; 
        cancelUrl?: string; 
    }): Promise<{ checkoutUrl: string; sessionId: string }> =>
        apiCall('/api/stripe/checkout', { method: 'POST', body: JSON.stringify(data) }),
    createPortalSession: (data?: { 
        returnUrl?: string; 
    }): Promise<{ portalUrl: string }> =>
        apiCall('/api/stripe/portal', { method: 'POST', body: JSON.stringify(data || {}) }),
};

// Usage API
export const usageApi = {
    getUsageInfo: (): Promise<{ 
        total: number; 
        remaining: number; 
        isUnlimited: boolean; 
        planType: string;
        limit: number | null;
    }> => apiCall('/api/usage'),
};

// Export API Types
interface ExportConfigResponse {
    config: SavedExportConfig;
}

interface ExportConfigsListResponse {
    configs: SavedExportConfig[];
    namingVariables: string[];
}

interface ExportCreateResponse {
    jobId: string;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    downloadUrl: string;
}

interface ExportStatusResponse {
    job: ExportJob;
}

interface ExportHistoryResponse {
    exports: ExportJob[];
}

// Export API
export const exportApi = {
    // Config management
    listConfigs: (includePublic = true): Promise<ExportConfigsListResponse> => 
        apiCall(`/api/export/configs?includePublic=${includePublic}`),
    
    saveConfig: (data: {
        name: string;
        description?: string;
        config: ExportConfig;
        isPublic?: boolean;
    }): Promise<ExportConfigResponse> => 
        apiCall('/api/export/configs', { 
            method: 'POST', 
            body: JSON.stringify(data) 
        }),
    
    deleteConfig: (id: string): Promise<{ success: boolean }> => 
        apiCall(`/api/export/configs?id=${id}`, { method: 'DELETE' }),
    
    // Export operations
    createExport: (data: {
        soundIds: string[];
        configId?: string;
        customConfig?: ExportConfig;
    }): Promise<ExportCreateResponse> => 
        apiCall('/api/export/create', { 
            method: 'POST', 
            body: JSON.stringify(data) 
        }),
    
    getExportStatus: (jobId: string): Promise<ExportStatusResponse> => 
        apiCall(`/api/export/status?jobId=${jobId}`),
    
    getExportHistory: (): Promise<ExportHistoryResponse> => 
        apiCall('/api/export/history'),
};

// Standalone function exports for backward compatibility
export const listProjects = projectsApi.list;
export const listFolders = foldersApi.list;
export const listSounds = soundsApi.list; 